import React, { useEffect, useRef, useState } from "react";
import { Menu, Tooltip } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { selectAuthInfo } from "../pages/Auth/authSelectors";

import {
  PieChartOutlined,
  StockOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  TeamOutlined,
  DownOutlined,
  UpOutlined,
  SolutionOutlined,
  CarOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import { ReactComponent as Sales } from "../assets/icons/products.svg";
import { ReactComponent as Service } from "../assets/icons/service.svg";
import { ReactComponent as Order } from "../assets/icons/FolderNotch.svg";
import { ReactComponent as Purchase } from "../assets/icons/purchase.svg";
import { ReactComponent as Accounts } from "../assets/icons/accounts_filled_icon.svg";
import { ReactComponent as Quotation } from "../assets/icons/BookOpen.svg";
import { ReactComponent as Crm } from "../assets/icons/crm.svg";
import { ReactComponent as MenuIcon } from "../assets/icons/icons8-menu-50.png";
import { ReactComponent as CrmLogo } from "../assets/icons/Logo-crm.svg";
import { ReactComponent as PharmaLogo } from "../assets/icons/pharma.svg";
import { ReactComponent as Pos } from "../assets/icons/pos_icon.svg";
import { ReactComponent as Hr } from "../assets/icons/hr.svg";
import { ReactComponent as Manufacture } from "../assets/icons/manufacture.svg";
import { ReactComponent as Inventry } from "../assets/icons/inventry.svg";
import { ReactComponent as Report } from "../assets/icons/reportsIcon.svg";
import { ReactComponent as Settings } from "../assets/icons/settings.svg";
import { useSelector } from "react-redux";
import { accessChecker, isAdmin } from "../lib/functions";
import { checkEmployeeRouteAccessApi } from "../apiservice/hrpayroll";
import { hasWorkflowAccess } from "../lib/workflowAccess";
import { Grid } from "antd";
// Replaced by management with the new TRIAC-branded logo, used for every
// subscription type now. This is the white+gold variant specifically, built
// for the sidebar's dark background -- the navy+gold variant used elsewhere
// (e.g. the login page) would be nearly invisible here. Not yet finalized --
// the old per-subscription logos are backed up under
// assets/icons/_old-logos-backup/ if this needs reverting (old forthapps
// logo: logo.svg; old pharma logo: pharma.svg).
import forthappsLogo from "../assets/icons/forthapps-logo-white.png";

// Extra bottom padding reserved on mobile so the down-arrow scroll overlay
// (30px tall) never sits on top of the last menu item.
const MOBILE_BOTTOM_RESERVE = 55;

const Sidebar = ({ onItemClick, collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const screens = Grid.useBreakpoint();
  const [appLogo, appLogoSet] = useState();
  const [appName, appNameSet] = useState("");
  const [subscriptionType, subscriptionTypeSet] = useState("");

  const [openKeys, setOpenKeys] = useState([]);

  const authInfo = useSelector(selectAuthInfo);
  const isSuperAdminUser = String(authInfo?.subscription || "").toLowerCase() === "supererp";
  const canUseWorkflows = hasWorkflowAccess(authInfo);
  const canUseATS = isSuperAdminUser || accessChecker("ats_candidates");
  const canUseTatmeenPacking = authInfo?.is_tatmeen_enabled == "custom";

  // "Work Routes" is accessible to any employee now (not just the Sales
  // Agent role, which also granted unrelated Sales Order/Supplier access),
  // but should only actually show for non-admin employees who have a route
  // assignment scheduled today -- admins keep seeing it unconditionally
  // since they manage routes for others rather than execute their own.
  const isEmployee = authInfo?.employee_id && authInfo.employee_id !== 0;
  const isAdminUser = isSuperAdminUser || isAdmin(authInfo);
  const [hasRouteAccess, setHasRouteAccess] = useState(false);

  useEffect(() => {
    if (!isEmployee || isAdminUser) return;

    let cancelled = false;
    checkEmployeeRouteAccessApi()
      .then((res) => {
        if (!cancelled) setHasRouteAccess(!!res?.data?.has_route_access);
      })
      .catch(() => {
        if (!cancelled) setHasRouteAccess(false);
      });

    return () => { cancelled = true; };
  }, [isEmployee, isAdminUser]);

  const rootSubmenuKeys = [
    "sales",
    "quotation",
    "order",
    "service",
    "purchase",
    "inventory",
    "accounts",
    "hrpayroll",
    "crm",
    "delivery",
  ];

  useEffect(() => {
    let path = window.location.hostname;

    if (path === "erp.forthapps.com" || path === "erp-dev.forthapps.com") {
      subscriptionTypeSet("ForthERP");
      appNameSet("ForthERP");
      appLogoSet(forthappsLogo);
    } else if (
      path === "pharmaerp.forthapps.com" ||
      path === "pharmaerp-dev.forthapps.com"
    ) {
      subscriptionTypeSet("PharmaERP");
      appNameSet("PharmaERP");
      appLogoSet(forthappsLogo);
    } else if (
      path === "pharmaacc.forthapps.com" ||
      path === "pharmaacc-dev.forthapps.com"
    ) {
      subscriptionTypeSet("PharmaBooks");
      appNameSet("PharmaERP");
      appLogoSet(forthappsLogo);
    } else if (
      path === "hrpayroll.forthapps.com" ||
      path === "hrpayroll-dev.forthapps.com"
    ) {
      subscriptionTypeSet("HRPayroll");
      appNameSet("HR-Payroll");
      appLogoSet(forthappsLogo);
    } else if (
      path === "accounts.forthapps.com" ||
      path === "accounts-dev.forthapps.com"
    ) {
      subscriptionTypeSet("ForthBooks");
      appNameSet("Forth Books");
      appLogoSet(forthappsLogo);
    } else if (path === "localhost") {
      subscriptionTypeSet("ForthBooks");
      appNameSet("Forth Books");
      appLogoSet(forthappsLogo);
    } else {
      subscriptionTypeSet("ForthERP");
      appNameSet("ForthERP");
      appLogoSet(forthappsLogo);
    }
  }, []);

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));

    if (!rootSubmenuKeys.includes(latestOpenKey)) {
      setOpenKeys(keys);
    } else {
      // Only allow one to be open
      setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const withTooltip = (label) => {
    // ❌ No tooltip on mobile / tablet
    if (!screens.md) {
      return <span>{label}</span>;
    }

    // ✅ Tooltip only on desktop
    return (
      <Tooltip placement="right" title={label}>
        <span>{label}</span>
      </Tooltip>
    );
  };



  const items = [
    {
      key: "/admin-dashboard",
      value: "admin-dashboard",
      label: withTooltip("Admin Dashboard"),
      icon: (
        <PieChartOutlined
          className="sideBarSvgIcon"
          style={{ fontSize: "24px" }}
        />
      ),
    },
    {
      key: "/dashboard",
      value: "dashboard",
      icon: (
        <PieChartOutlined
          className="sideBarSvgIcon"
          style={{ fontSize: "24px" }}
        />
      ),
      label: withTooltip("Dashboard"),
    },
    {
      key: "/workflows",
      value: "workflows",
      hidden: !canUseWorkflows,
      // ✅ Role-gated like every other menu (Reports, Sales, HR & Payroll, ...):
      // visibility now also requires the "workflows" tab in role_access, so an
      // employee-only role that hasn't been granted it won't see this link.
      icon: (
        <DatabaseOutlined
          className="sideBarSvgIcon"
          style={{ fontSize: "24px" }}
        />
      ),
      label: withTooltip("Workflows"),
    },
    {
      key: "sales",
      value: "sales",
      icon: (
        <StockOutlined
          className="sideBarSvgIcon"
          style={{ fontSize: "24px" }}
        />
      ),
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation(); // prevent expand
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/sales-dashboard"); // go to sales dashboard
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Sales
        </span>
      ),
      children: [
        {
          key: "/sales-dashboard",
          value: "sales",
          label: null,
          style: { display: "none" },
        },
        {
          key: "/customers",
          value: "customer_entry",
          label: "Customers",
        },
        // {
        //   key: "/sales/doctor-overview",
        //   value: "doctor_overview",
        //   label: withTooltip("Doctor Overview"),
        // },
        // {
        //   key: "/sales/patient-overview",
        //   value: "patient_overview",
        //   label: withTooltip("Patient Overview"),
        // },
        // {
        //   key: "/sales-replace",
        //   value: "product_replace",
        //   label: withTooltip("Product Replace"),
        // },
        {
          key: "/sales",
          value: "new_sales",
          label: "Sales",
        },
        {
          key: "/sales-returns",
          value: "sales_return",
          label: withTooltip("Sales Return"),
        },
      ],
    },
    {
      key: "/pos/pos-entry",
      value: "pos",
      icon: <Pos className="sideBarSvgIcon" style={{ fontSize: "24px" }} />,
      label: "POS",
    },
    {
      key: "quotation",
      value: "quotation",
      icon: (
        <FileTextOutlined
          className="sideBarSvgIcon"
          style={{ fontSize: "24px" }}
        />
      ),
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/quotation/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Quotation
        </span>
      ),
      children: [
        {
          key: "/quotation/dashboard",
          label: null,
          style: { display: "none" },
        },
        {
          key: "/quotations",
          value: "quotation_entry",
          label: "Quotations",
        },
      ],
    },
    {
      key: "order",
      value: "order",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/order/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Order
        </span>
      ),
      icon: <Order className="sideBarSvgIcon" style={{ fontSize: "24px" }} />,
      children: [
        {
          key: "/order/purchase",
          value: "purchase_order_entry",
          label: withTooltip("Purchase Order"),
        },
        {
          key: "/order/sales",
          value: "sales_order_entry",
          label: withTooltip("Sales Order"),
        },
      ],
    },
    {
      key: "service",
      value: "service",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/service/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Service
        </span>
      ),
      icon: <Service className="sideBarSvgIcon" style={{ fontSize: "24px" }} />,
      children: [
        {
          key: "/service",
          value: "service_entry",
          label: "Service",
        },
        {
          key: "/service-return",
          value: "service_return",
          label: withTooltip("Service Return"),
        },
        {
          key: "/service-expense",
          value: "service_expense_entry",
          label: withTooltip("Service Expense"),
        },
      ],
    },
    {
      key: "purchase",
      value: "purchase",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/purchase/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Purchase
        </span>
      ),
      icon: (
        <Purchase className="sideBarSvgIcon" style={{ fontSize: "24px" }} />
      ),
      children: [
        {
          key: "/purchase",
          value: "purchase_entry",
          label: "Purchase",
        },
        {
          key: "/purchase-return",
          value: "purchase_return",
          label: withTooltip("Purchase Return"),
        },
      ],
    },
    {
      key: "tatmeen",
      value: "tatmeen",
      hidden: !canUseTatmeenPacking,
      alwaysShow: true,
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/tatmeen");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Tatmeen
        </span>
      ),
      icon: (
        <QrcodeOutlined className="sideBarSvgIcon" style={{ fontSize: "24px" }} />
      ),
      children: [
        {
          key: "/tatmeen?tab=packing",
          value: "tatmeen_entry",
          label: "Packing",
        },
        {
          key: "/tatmeen?tab=record",
          value: "tatmeen_entry",
          label: "Packing Record",
        },
        {
          key: "/tatmeen?tab=shipping",
          value: "tatmeen_entry",
          label: "Shipping",
        },
        {
          key: "/tatmeen?tab=shipping-record",
          value: "tatmeen_entry",
          label: "Shipping Record",
        },
      ],
    },
    {
      key: "/manufacturing",
      value: "manufacturing",
      icon: (
        <Manufacture className="sideBarSvgIcon" style={{ fontSize: "24px" }} />
      ),
      label: "Manufacturing",
    },
    {
      key: "inventory",
      value: "inventory",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/inventory/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Inventory
        </span>
      ),
      icon: (
        <DatabaseOutlined
          className="sideBarSvgIcon"
          style={{ fontSize: "24px" }}
        />
      ),
      children: [
        {
          key: "/inventory/product",
          value: "item_entry",
          label: "Items",
        },
        {
          key: "/inventory/manufacturers",
          value: "item_entry",
          label: "Manufacturers",
        },
        {
          key: "/inventory/item-stock-report",
          value: "item_stock_report",
          label: withTooltip("Item Stock Report"),
        },
        {
          key: "/inventory/item-adjustment",
          value: "item_adjustment_entry",
          label: withTooltip("Item Adjustment"),
        },
        {
          key: "/inventory/item-ledger",
          value: "item_ledger",
          label: withTooltip("Item Ledger"),
        },
        {
          key: "/inventory/category",
          value: "item_category",
          label: "Category",
        },
        {
          key: "/inventory/generic-names",
          value: "generic_name",
          label: "Generic Names",
        },
        {
          key: "/inventory/item-group-entry",
          value: "group_entry",
          label: withTooltip("Group Entry"),
        },
        {
          key: "/inventory/unit-manage",
          value: "item_unit",
          label: withTooltip("Unit Entry"),
        },
        {
          key: "/inventory/models-manage",
          value: "model_manage",
          label: "Models",
        },
        {
          key: "/inventory/origins-manage",
          value: "origin_manage",
          label: "Origins",
        },
        {
          key: "/inventory/racks-manage",
          value: "rack_manage",
          label: "Racks",
        },
        {
          key: "/inventory/transfer-entry",
          value: "transfer_entry",
          label: withTooltip("Transfer Entry"),
        },
        {
          key: "/inventory/transfer-pending-record",
          value: "transfer_pending_record",
          label: withTooltip("Transfer Pending Record"),
        },
        {
          key: "/inventory/transfer-receive-record",
          value: "transfer_receive_record",
          label: withTooltip("Transfer Received Record"),
        },
        {
          key: "/inventory/transfer-record",
          value: "transfer_record",
          label: withTooltip("Transfer Record"),
        },
        {
          key: "/inventory/adjustment-record",
          value: "adjustment_record",
          label: withTooltip("Adjustment Record"),
        },
        {
          key: "/inventory/heirrachy-flow",
          value: "tatmeen_entry",
          label: withTooltip("Heirrachy Flow"),
        },
      ]
    },
    {
      key: "accounts",
      value: "accounts",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/accounts/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Accounts
        </span>
      ),
      icon: (
        <Accounts className="sideBarSvgIcon" style={{ fontSize: "24px" }} />
      ),
      children: [
        {
          key: "/accounts/account-entry",
          value: "account_entry",
          label: "Chart of Accounts",
        },
        {
          key: "/accounts/income",
          value: "income_entry",
          label: "Income",
        },
        {
          key: "/accounts/expenses",
          value: "expense_entry",
          label: "Expenses",
        },
        {
          key: "/accounts/debitor-receipts",
          value: "debtor_receipt",
          label: withTooltip("Receipts"),
        },
        {
          key: "/accounts/creditor-payments",
          value: "creditor_payment",
          label: withTooltip("Payments"),
        },
        {
          key: "/accounts/recurring-journals",
          value: "recurring_journal_entry",
          label: withTooltip("Recurring Journals"),
        },
        {
          key: "/accounts/journal-entry",
          value: "journal_entry",
          label: withTooltip("Journal / Double"),
        },
        {
          key: "/accounts/contra-entry",
          value: "contra_entry",
          label: withTooltip("Contra / Single"),
        },
        {
          key: "/accounts/bank-transactions",
          value: "bank_transactions",
          label: withTooltip("Bank Transactions"),
        },
        {
          key: "/accounts/cheques",
          value: "cheque_entry",
          label: "Cheques",
        },
        {
          key: "/accounts/advance-tran",
          value: "advance_transaction_entry",
          label: withTooltip("Advance Transactions"),
        },
        {
          key: "/accounts/branch-tran",
          value: "branch_tran",
          label: withTooltip("Branch Transactions"),
        },
        {
          key: "/reports/branch-inward",
          value: "branch_tran",
          label: withTooltip("Branch Transactions Inward"),
        },

        {
          key: "/accounts/expense-recognitions",
          value: "expense_recognition_entry",
          label: withTooltip("Expense Recognitions"),
        },
        {
          key: "/accounts/opening-balances",
          value: "account_entry",
          label: withTooltip("Opening Balances"),
        },
      ]
    },
    {
      key: "hrpayroll",
      value: "hrpayroll",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/hrpayroll/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          HR & Payroll
        </span>
      ),
      icon: <Hr className="sideBarSvgIcon" style={{ fontSize: "24px" }} />,
      // children: [
      //   {
      //     key: "/hrpayroll/attendance",
      //     value: "attendance_entry",
      //     label: "Attendance",
      //   },
      //   // {
      //   //   key: "/hrpayroll/attendance-overview",
      //   //   value: "attendance_overview",
      //   //   label: withTooltip("Attendance Overview"),
      //   // },
      //   {
      //     key: "/hrpayroll/hrlocation",
      //     value: "hrlocation",
      //     label: withTooltip("Branch Location"),
      //   },
      //   {
      //     key: "/hrpayroll/clients-entry",
      //     value: "clients_entry",
      //     label: "Clients",
      //   },
      //   {
      //     key: "/hrpayroll/departments",
      //     value: "department_entry",
      //     label: "Departments",
      //   },
      //   {
      //     key: "/hrpayroll/designations",
      //     value: "designation_entry",
      //     label: "Designations",
      //   },
      //   {
      //     key: "/hrpayroll/onboarding",
      //     value: "onboarding",
      //     label: "Onboarding",
      //   },
      //   {
      //     key: "/hrpayroll/employees",
      //     value: "employee_entry",
      //     label: "Employees",
      //   },
      //   {
      //     key: "/hrpayroll/work-routes",
      //     value: "work_routes",
      //     label: "Work Routes",
      //   },
      //   {
      //     key: "/hrpayroll/hr-settings-view",
      //     value: "hr_settings_view",
      //     label: withTooltip("HR Settings View"),
      //   },
      //   {
      //     key: "/hrpayroll/monthly-salary-report",
      //     value: "monthly_salary_report",
      //     label: withTooltip("Monthly Salary Report"),
      //   },

      //   {
      //     key: "/hrpayroll/salary-report",
      //     value: "salary_report",
      //     label: withTooltip("Salary Payment Report"),
      //   },
      //   {
      //     key: "/hrpayroll/salary-payments",
      //     value: "salary_payment",
      //     label: withTooltip("Salary Payments"),
      //   },
      // ],
      children: [
        {
          key: "/hrpayroll/employees",
          value: "employee_entry",
          label: "Employees",
        },
        {
          key: "/hrpayroll/work-routes",
          value: "work_routes",
          label: "Work Routes",
        },
        {
          key: "/hrpayroll/attendance",
          value: "attendance_entry",
          label: "Attendance",
        },
        {
          key: "/hrpayroll/salary-payments",
          value: "salary_payment",
          label: withTooltip("Salary Payments"),
        },
        {
          key: "/hrpayroll/monthly-salary-report",
          value: "monthly_salary_report",
          label: withTooltip("Monthly Salary Report"),
        },
        {
          key: "/hrpayroll/salary-report",
          value: "salary_report",
          label: withTooltip("Salary Payment Report"),
        },
        {
          key: "/hrpayroll/onboarding",
          value: "onboarding",
          label: "Onboarding",
        },
        {
          key: "/hrpayroll/clients-entry",
          value: "clients_entry",
          label: "Clients",
        },
        {
          key: "/hrpayroll/departments",
          value: "department_entry",
          label: "Departments",
        },
        {
          key: "/hrpayroll/designations",
          value: "designation_entry",
          label: "Designations",
        },
        {
          key: "/hrpayroll/hrlocation",
          value: "hrlocation",
          label: withTooltip("Branch Location"),
        },
        {
          key: "/hrpayroll/hr-settings-view",
          value: "hr_settings_view",
          label: withTooltip("HR Settings"),
        },
      ],
    },
    {
      key: "/reports",
      value: "reports",
      icon: <Report className="sideBarSvgIcon" style={{ fontSize: "24px" }} />,
      label: "Reports",
    },
    {
      key: "crm",
      value: "crm",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick(); // ✅ close sidebar on mobile
            }
            navigate("/crm/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          CRM
        </span>
      ),
      icon: (
        <TeamOutlined className="sideBarSvgIcon" style={{ fontSize: "24px" }} />
      ),
      children: [
        {
          key: "/crm/leads",
          value: "crm_leads",
          label: "Leads",
        },
        {
          key: "/crm/deals",
          value: "crm_deals",
          label: "Deals",
        },
        {
          key: "/crm/tasks",
          value: "crm_tasks",
          label: "Tasks",
        },
        {
          key: "/crm/customize",
          value: "crm_customize",
          label: "Customization",
        },

        // {
        //   key: "/crm/customer-approved-list",
        //   value: "approved_customer_list",
        //   label: withTooltip("Approved Customers"),
        // },
        {
          key: "/crm/user-wise-customer-report",
          value: "employee_wise_customer_list",
          label: withTooltip("CRM Report"),
        },
        // {
        //   key: "/crm/customers",
        //   value: "crm_customer_entry",
        //   label: "Customers",
        // },
        // {
        //   key: "/crm/customer-pending-list",
        //   value: "pending_customer_list",
        //   label: withTooltip("Pending Customers"),
        // },

        // {
        //   key: "/crm/customer-rejected-list",
        //   value: "rejected_customer_list",
        //   label: withTooltip("Rejected Customers"),
        // },
      ],
    },
    canUseATS && {
      key: "ats",
      value: "ats",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) {
              onItemClick();
            }
            navigate("/ats/dashboard");
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.textDecoration = "underline")
          }
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          ATS
        </span>
      ),
      icon: (
        <TeamOutlined className="sideBarSvgIcon" style={{ fontSize: "24px" }} />
      ),
      children: [
        { key: "/ats/jobs", value: "ats_jobs", label: "Jobs" },
        {
          key: "/ats/candidates",
          value: "ats_candidates",
          label: "Candidates",
        },
        { key: "/ats/clients", value: "ats_clients", label: "Clients" },
        { key: "/ats/agencies", value: "ats_agencies", label: "Agencies" },
        { key: "/ats/workflows", value: "ats_workflows", label: "Workflows" },
        { key: "/ats/tasks", value: "ats_workflow_tasks", label: "Tasks" },
      ],
    },
    (isSuperAdminUser || accessChecker("delivery_jobs_manage") || accessChecker("delivery_routes_manage")) && {
      key: "delivery",
      value: "delivery",
      label: (
        <span
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!screens.md) onItemClick();
            navigate("/delivery/dashboard");
          }}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Delivery
        </span>
      ),
      icon: <CarOutlined className="sideBarSvgIcon" style={{ fontSize: "24px" }} />,
      children: [
        { key: "/delivery/dashboard", value: "delivery_jobs_manage", label: "Dashboard" },
        { key: "/delivery/delivery-execution", value: "delivery_jobs_manage", label: "Delivery Execute" },
        { key: "/delivery/jobs", value: "delivery_jobs_manage", label: "Jobs" },
        { key: "/delivery/pickup", value: "delivery_jobs_manage", label: "Pickup" },
        { key: "/delivery/routes", value: "delivery_routes_manage", label: "Routes" },
        { key: "/delivery/route-points", value: "delivery_routes_manage", label: "Route Points" },
        { key: "/delivery/areas", value: "delivery_routes_manage", label: "Areas" },
        { key: "/delivery/assignments", value: "delivery_routes_manage", label: "Assignments" },
        { key: "/delivery/live-tracking", value: "delivery_routes_manage", label: "Live Tracking" },
        { key: "/delivery/status-flow", value: "delivery_routes_manage", label: "Status Flow" },
        { key: "/delivery/users", value: "delivery_routes_manage", label: "Delivery Users" },
      ],
    },
    {
      key: "/settings",
      value: "settings",
      label: "Settings",
      icon: <Settings className="sideBarSvgIcon" width={24} height={24} />,
    },
  ].filter(Boolean);

  if (
    authInfo?.branch_company_type === "Pharmacy" ||
    authInfo?.subscription === "SuperERP"
  ) {
    const salesMenu = items.find((item) => item.key === "sales");


    if (salesMenu) {
      const invoiceIndex = salesMenu.children.findIndex(
        (child) => child.key === "/sales"
      );
      console.log(invoiceIndex, "salesMenu11");
      console.log(salesMenu, "salesMenu1133");

      if (invoiceIndex !== -1) {
        salesMenu.children.splice(invoiceIndex, 1);
        salesMenu.children.splice(
          invoiceIndex,
          0,
          {
            key: "/sales/patient-overview",
            value: "patient_overview",
            label: "Patient",
          },
          {
            key: "/sales/doctor-overview",
            value: "doctor_overview",
            label: "Doctor",
          },
          {
            key: "/sales",
            value: "pharma_sales",
            label: "New Invoice",
          },
        );
      }
    }

    const accountsMenu = items.find((item) => item.key === "accounts");

    if (
      accountsMenu &&
      (authInfo?.branch_company_type === "Pharmacy" ||
        authInfo?.subscription === "SuperERP")
    ) {
      accountsMenu.children.splice(1, 0, {
        key: "/accounts/debitor-pharma-receipts",
        value: "debitor_pharma_receipts",
        label: "Insurance Receipts",
      });
    }
  }

  if (!accessChecker("sales_order_entry")) {
    const orderMenu = items.find((item) => item.key == "order");
    if (
      accessChecker("sales_order_record") ||
      !accessChecker("sales_order_voucher")
    ) {
      orderMenu.children.push({
        key: "/order/sales",
        value: "sales_order_record",
        label: "Sales Order",
        link: "/order/sales",
      });
    } else if (
      !accessChecker("sales_order_record") ||
      accessChecker("sales_order_voucher")
    ) {
      orderMenu.children.push({
        key: "/order/sales",
        value: "sales_order_voucher",
        label: "Sales Order",
        link: "/order/sales",
      });
    }
  }

  if (!accessChecker("purchase_order_entry")) {
    const orderMenu = items.find((item) => item.key == "order");
    if (
      accessChecker("purchase_order_record") ||
      !accessChecker("purchase_order_voucher")
    ) {
      orderMenu.children.push({
        key: "/order/purchase",
        value: "purchase_order_record",
        label: "Purchase Order",
        link: "/order/purchase",
      });
    } else if (
      !accessChecker("purchase_order_record") ||
      accessChecker("purchase_order_voucher")
    ) {
      orderMenu.children.push({
        key: "/order/purchase",
        value: "purchase_order_voucher",
        label: "Purchase Order",
        link: "/order/purchase",
      });
    }
  }

  if (!accessChecker("service_entry")) {
    const serviceMenu = items.find((item) => item.key == "service");
    if (accessChecker("service_record") || !accessChecker("service_voucher")) {
      serviceMenu.children.push({
        key: "/service",
        value: "service_record",
        label: "Service",
        link: "/service",
      });
    } else if (
      !accessChecker("service_record") ||
      accessChecker("service_voucher")
    ) {
      serviceMenu.children.push({
        key: "/service",
        value: "service_voucher",
        label: "Service",
        link: "/service",
      });
    }
  }

  if (!accessChecker("service_return")) {
    const serviceMenu = items.find((item) => item.key == "service");
    if (accessChecker("service_return_record") || accessChecker("service_return_voucher")) {
      serviceMenu.children.push({
        key: "/service-return",
        value: accessChecker("service_return_record") ? "service_return_record" : "service_return_voucher",
        label: "Service Return",
        link: "/service-return",
      });
    }
  }

  if (!accessChecker("service_expense_entry")) {
    const serviceMenu = items.find((item) => item.key == "service");
    if (
      accessChecker("service_expense_record") ||
      !accessChecker("service_expense_voucher")
    ) {
      serviceMenu.children.push({
        key: "/service-expense",
        value: "service_expense_record",
        label: "Service Expense",
        link: "/service",
      });
    } else if (
      !accessChecker("service_expense_record") ||
      accessChecker("service_expense_voucher")
    ) {
      serviceMenu.children.push({
        key: "/service-expense",
        value: "service_expense_voucher",
        label: "Service Expense",
        link: "/service",
      });
    }
  }

  if (!accessChecker("purchase_entry")) {
    const purchaseMenu = items.find((item) => item.key == "purchase");
    if (
      accessChecker("purchase_record") &&
      !accessChecker("purchase_voucher") &&
      !accessChecker("supplier_entry")
    ) {
      purchaseMenu.children.push({
        key: "/purchase",
        value: "purchase_record",
        label: "Purchase",
        link: "/purchase",
      });
    } else if (
      !accessChecker("purchase_record") &&
      accessChecker("purchase_voucher") &&
      !accessChecker("supplier_entry")
    ) {
      purchaseMenu.children.push({
        key: "/purchase",
        value: "purchase_voucher",
        label: "Purchase",
        link: "/purchase",
      });
    } else if (
      !accessChecker("purchase_record") &&
      !accessChecker("purchase_voucher") &&
      accessChecker("supplier_entry")
    ) {
      purchaseMenu.children.push({
        key: "/purchase",
        value: "supplier_entry",
        label: "Purchase",
        link: "/purchase",
      });
    } else if (
      accessChecker("purchase_record") &&
      accessChecker("purchase_voucher") &&
      accessChecker("supplier_entry")
    ) {
      purchaseMenu.children.push({
        key: "/purchase",
        value: "purchase_record",
        label: "Purchase",
        link: "/purchase",
      });
    }
  }

  if (!accessChecker("purchase_return")) {
    const purchaseMenu = items.find((item) => item.key == "purchase");
    if (
      accessChecker("purchase_return_record") ||
      !accessChecker("purchase_return_voucher")
    ) {
      purchaseMenu.children.push({
        key: "/purchase-return",
        value: "purchase_return_record",
        label: "Purchase Return",
        link: "/purchase-return",
      });
    } else if (
      !accessChecker("purchase_return_record") ||
      accessChecker("purchase_return_voucher")
    ) {
      purchaseMenu.children.push({
        key: "/purchase-return",
        value: "purchase_return_voucher",
        label: "Purchase Return",
        link: "/purchase-return",
      });
    }
  }

  const safeSort = (arr) =>
    arr.sort((a, b) => {
      const aLabel =
        typeof a.label === "string" ? a.label : String(a.label || "");
      const bLabel =
        typeof b.label === "string" ? b.label : String(b.label || "");
      return aLabel.localeCompare(bLabel);
    });

  const filteredMenuData = items
    .filter((menuItem) =>
      !menuItem.hidden &&
      (isSuperAdminUser ||
        (!authInfo?.disabled_modules?.includes(menuItem.value) &&
          (menuItem.alwaysShow ||
            authInfo?.tab?.some((tab) => tab.tab === menuItem.value))))
    )
    .map((menuItem) => {
      if (menuItem.children) {
        const filteredChildren = menuItem.children.filter((child) => {
          const moduleEnabled = isSuperAdminUser || !authInfo?.disabled_modules?.includes(child.value);
          if (!moduleEnabled) return false;

          if (isSuperAdminUser) return true;

          // Work Routes is now open to every employee role (not just Sales
          // Agent), but for non-admin employees it should only actually show
          // when they have a route assignment scheduled today -- admins see
          // it unconditionally via their role's own accessList below, since
          // they manage routes rather than execute their own.
          if (child.value === "work_routes" && isEmployee && !isAdminUser) {
            return hasRouteAccess;
          }

          return accessChecker(child.value);
        });

        return {
          ...menuItem,
          children: filteredChildren,
          //  children: safeSort(filteredChildren),
        };
      }
      return menuItem;
    })
    .filter(Boolean);

  // The Menu reserves extra bottom padding on mobile (see MOBILE_BOTTOM_RESERVE
  // below) so the last item isn't hidden behind the down-arrow overlay. That
  // padding must be excluded here too, otherwise the overlay stays visible
  // (and covers the last item, e.g. "Settings") until the user scrolls past
  // the empty padding itself.
  const checkScroll = () => {
    if (menuRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = menuRef.current;
      const bottomReserve = !screens.md ? MOBILE_BOTTOM_RESERVE : 0;
      setCanScrollUp(scrollTop > 0);
      setCanScrollDown(scrollTop + clientHeight < scrollHeight - bottomReserve);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = menuRef.current;
    if (el) el.addEventListener("scroll", checkScroll);
    return () => el && el.removeEventListener("scroll", checkScroll);
  }, []);

  const scrollBy = (offset) => {
    if (menuRef.current) {
      menuRef.current.scrollBy({ top: offset, behavior: "smooth" });
    }
  };

  const handleClick = (e) => {
    onItemClick();
    // Navigate to the selected menu item's key (path)
    navigate(e.key);
  };

  const handleLogoClick = () => {

    if (location.pathname === "/dashboard") return;
    navigate("/");
  };


  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        overflow: "hidden",
        background: "#001529",
        borderBottom: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      {/* Spacer ONLY for mobile screens */}
      {!screens.md && <div style={{ height: "50px" }}></div>}

      {/* Logo Section */}
      <div
        className="sidebar-logo-container"
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          padding: collapsed ? "0" : "0 16px",
          background: "#001529",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          transition: "all 0.3s ease-in-out",
        }}
      >
        {collapsed ? (
          // Sized by width, not height: this wordmark's aspect ratio is
          // wide/short (it carries a tagline), so fixing its height and
          // letting width follow overflowed the 80px collapsed rail. Width
          // + auto height adapts to whatever container it's actually in
          // (80px collapsed rail here; the same img is also used at 200px
          // expanded and in the mobile drawer's 60vw panel below).
          <img
            onClick={handleLogoClick}
            src={appLogo}
            alt="Logo"
            style={{
              width: "100%",
              maxWidth: 56,
              height: "auto",
              cursor: "pointer",
            }}
          />
        ) : (
          <img
            onClick={handleLogoClick}
            src={appLogo}
            alt="Logo"
            style={{
              width: "100%",
              maxWidth: 170,
              height: "auto",
              cursor: "pointer",
            }}
          />
        )}
      </div>

      {/* Scrollable Menu */}
      <div
        ref={menuRef}
        style={{
          height: "calc(100% - 64px)",
          overflowY: "auto",
          scrollbarWidth: "none",
        }}
      >
        <Menu
          // defaultSelectedKeys={["1"]}
          // defaultOpenKeys={["sub1"]}
          mode="inline"
          theme="dark"
          items={filteredMenuData}
          onClick={handleClick} // Handle menu item click
          selectedKeys={[
            location.pathname === "/tatmeen"
              ? `/tatmeen?tab=${new URLSearchParams(location.search).get("tab") || "packing"}`
              : location.pathname,
          ]}
          defaultOpenKeys={["sales"]}
          style={{
            fontSize: "13.5px", // 👈 reduce label font size
            lineHeight: "36px", // optional: adjust row height
            paddingBottom: !screens.md && `${MOBILE_BOTTOM_RESERVE}px`,
          }}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          inlineCollapsed={collapsed}
        />
      </div>

      {/* Up arrow */}
      {canScrollUp && (
        <div
          onClick={() => scrollBy(-100)}
          style={{
            position: "absolute",
            top: 64, // below logo
            left: 0,
            right: 0,
            height: 30,
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            zIndex: 10,
          }}
        >
          <UpOutlined />
        </div>
      )}

      {/* Down arrow */}
      {canScrollDown && (
        <div
          onClick={() => scrollBy(100)}
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 30,
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
            zIndex: 10,
          }}
        >
          <DownOutlined />
        </div>
      )}
    </div>
  );
};

export default Sidebar;
